import { useQuery } from "@tanstack/react-query";
import { runtimeApi } from "@/api/runtime";
import type { RuntimeInfo } from "@/api/runtime";

export const RUNTIME_INFO_QUERY_KEY = ["runtime", "info"] as const;

/**
 * Reads the API's runtime metadata once per session (long staleTime, no
 * refetch-on-focus — the env doesn't change for the life of a tab).
 */
export function useRuntimeInfo() {
  return useQuery({
    queryKey: RUNTIME_INFO_QUERY_KEY,
    queryFn: () => runtimeApi.info(),
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/**
 * True when the respondent web-interview surface is enabled on this
 * deployment. Defaults to `false` while the query is in flight so we never
 * flash the interview UI on a deployment that has it gated off.
 */
export function useWebInterviewEnabled(): boolean {
  const { data } = useRuntimeInfo();
  return Boolean((data as RuntimeInfo | undefined)?.features?.web_interview);
}

/** True when the respondent own-twin chat surface is enabled (used to show
 *  the "talk to your twin" teaser once the interview is promoted). */
export function useRespondentChatEnabled(): boolean {
  const { data } = useRuntimeInfo();
  return Boolean((data as RuntimeInfo | undefined)?.features?.respondent_chat);
}
